import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Check, Palette } from 'lucide-react';
import clsx from 'clsx';

import { DEFAULT_BRAND_COLORS } from '../../config';
import { useMediaStudio } from '../../context/MediaStudioContext';
import { TEXT_UI_ATTR } from '../../elements/text/TextEditOverlay';
import { isLightColor, normalizeHex } from '../../utils/color';

import scss from './ColorSwatch.module.scss';

function ColorSwatch({ value, title, onChange, onDone, small = false }) {
  const { t } = useTranslation('mediaStudio');
  const { features } = useMediaStudio();
  const assetColors = useSelector((state) => state.mediaStudioSettings.colors);
  const brandColors = assetColors.length ? assetColors : DEFAULT_BRAND_COLORS;
  const [anchor, setAnchor] = useState(null);
  const current = normalizeHex(value);
  const [hexDraft, setHexDraft] = useState(current || '');
  const showBrandColors = features.brandColors && brandColors.length > 0;

  useEffect(() => {
    setHexDraft(current || '');
  }, [current]);

  const close = () => {
    setAnchor(null);
    onDone?.();
  };

  const pick = (hex) => {
    onChange(hex);
    close();
  };

  const commitHexDraft = () => {
    const hex = normalizeHex(hexDraft);
    if (hex && hex !== current) {
      onChange(hex);
      onDone?.();
    } else {
      setHexDraft(current || '');
    }
  };

  const handleClickAway = (event) => {
    if (!anchor?.contains(event.target)) {
      close();
    }
  };

  return (
    <>
      <button
        type='button'
        className={clsx(scss.swatch, small && scss.swatchSmall, value == null && scss.swatchMixed)}
        title={title}
        aria-label={title}
        aria-haspopup='dialog'
        aria-expanded={Boolean(anchor)}
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => (anchor ? close() : setAnchor(event.currentTarget))}>
        <span className={scss.swatchColor} style={value == null ? undefined : { background: value }} />
      </button>

      <Popper
        open={Boolean(anchor)}
        anchorEl={anchor}
        placement='bottom-start'
        className={scss.popper}
        modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
        {...{ [TEXT_UI_ATTR]: '' }}>
        <ClickAwayListener onClickAway={handleClickAway}>
          <div className={scss.panel} role='dialog' aria-label={title}>
            <div className={scss.header}>
              <span className={clsx(scss.preview, !current && scss.previewMixed)}>
                <span style={current ? { background: current } : undefined} />
              </span>
              <input
                className={scss.hexInput}
                value={hexDraft}
                placeholder='#000000'
                spellCheck={false}
                aria-label={t('hexColor')}
                onChange={(event) => setHexDraft(event.target.value)}
                onBlur={commitHexDraft}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    commitHexDraft();
                  }
                  if (event.key === 'Escape') {
                    close();
                  }
                }}
              />
            </div>

            {showBrandColors ? (
              <section className={scss.group}>
                <h5 className={scss.title}>{t('brandColors')}</h5>
                <div className={scss.swatches}>
                  {brandColors.map((brandColor) => {
                    const active = current === brandColor.color;
                    return (
                      <button
                        key={brandColor.id}
                        type='button'
                        className={clsx(scss.brandSwatch, active && scss.brandSwatchActive)}
                        style={{ background: brandColor.color }}
                        title={`${brandColor.label} · ${brandColor.color}`}
                        aria-label={brandColor.label}
                        aria-pressed={active}
                        onClick={() => pick(brandColor.color)}>
                        {active ? (
                          <Check
                            size={14}
                            strokeWidth={3}
                            className={isLightColor(brandColor.color) ? scss.checkDark : scss.checkLight}
                          />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}

            <label className={scss.custom}>
              <span className={scss.spectrum} />
              <span className={scss.customLabel}>{t('customColor')}</span>
              <Palette size={15} />
              <input
                type='color'
                aria-label={t('customColor')}
                value={current || '#000000'}
                onChange={(event) => onChange(event.target.value.toUpperCase())}
                onBlur={onDone}
              />
            </label>
          </div>
        </ClickAwayListener>
      </Popper>
    </>
  );
}

export default ColorSwatch;
